import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Ship, Landmark, Waves, Mountain, Users, PartyPopper, ShieldCheck, CreditCard, CalendarCheck, ArrowRight } from "lucide-react";
import { api, logError } from "@/lib/apiClient";
import { useI18n } from "@/i18n";
import { ActivityCard } from "@/components/ActivityCard";
import { Button } from "@/components/ui/button";
import { UnderConstructionOverlay } from "@/components/UnderConstructionOverlay";

const ICONS = { Ship, Landmark, Waves, Mountain, Users, PartyPopper };
const HERO = "https://images.unsplash.com/photo-1702917676247-33298df069a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhbGFueWElMjB0dXJrZXklMjBiZWFjaCUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODYyMTQ4ODV8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch((e) => logError("categories load failed", e));
    api.get("/activities", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6))).catch((e) => logError("featured load failed", e));
  }, []);

  const search = () => navigate(`/activities${query ? `?search=${encodeURIComponent(query)}` : ""}`);

  return (
    <div>
      <UnderConstructionOverlay />
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[85vh] min-h-[560px] w-full overflow-hidden">
          <img src={HERO} alt="Alanya" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-white text-sm font-medium mb-5 border border-white/20">
                <ShieldCheck className="h-4 w-4" /> {t("verified")} · Alanya, Turkey
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
                {t("hero_title")}
              </h1>
              <p className="mt-5 text-lg text-white/90 max-w-xl">{t("hero_sub")}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 w-full max-w-2xl">
              <div className="glass rounded-2xl p-2 flex items-center gap-2 soft-shadow">
                <Search className="h-5 w-5 text-muted-foreground ml-3 shrink-0" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder={t("search_placeholder")}
                  className="flex-1 bg-transparent outline-none py-2.5 text-sm sm:text-base min-w-0"
                />
                <Button data-testid="hero-search-btn" onClick={search} className="rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground shrink-0">
                  {t("explore")}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 -mt-14 relative z-10">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {categories.map((c, i) => {
            const Icon = ICONS[c.icon] || Ship;
            return (
              <motion.button
                key={c.id}
                data-testid={`category-${c.id}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => navigate(`/activities?category=${c.id}`)}
                className="rounded-2xl bg-white soft-shadow border border-black/5 p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-transform"
              >
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                <span className="text-xs sm:text-sm font-semibold text-center leading-tight">{c.name}</span>
                <span className="text-[11px] text-muted-foreground">{c.count}</span>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-accent">{t("browse_category")}</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mt-1">{t("featured")}</h2>
          </div>
          <Link to="/activities" data-testid="see-all-link" className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary hover:gap-2 transition-all">
            {t("nav_activities")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((a, i) => <ActivityCard key={a.id} activity={a} index={i} />)}
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12 relative overflow-hidden">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-8">{t("trust_title")}</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: t("verified"), desc: "Every operator is checked and rated by real travelers." },
              { icon: CreditCard, title: "Secure payment", desc: "Pay safely online with card. Your booking is protected." },
              { icon: CalendarCheck, title: t("free_cancel"), desc: "Flexible plans with clear cancellation policies." },
            ].map((f) => (
              <div key={f.title} className="flex gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/15 shrink-0"><f.icon className="h-5 w-5" /></span>
                <div>
                  <p className="font-semibold font-display">{f.title}</p>
                  <p className="text-sm text-primary-foreground/80 mt-1">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
